import React, { useEffect, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'

import { enablePinchZoom } from 'utils/pdfViewer'
import { useWindowDimensions } from 'utils/hooks/useWindowDimensions'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const INITIAL_SCALE = 0.75
const PADDING_SIZE = 128
const PDF_OPTIONS = { isEvalSupported: false }

const PDFViewerLoadingState = () => (
  <div
    className="w-screen flex items-center justify-center"
    style={{ height: 'calc(100vh - 54px)' }}
  >
    <Text color="inverse" variant="body">
      Memuat dokumen...
    </Text>
  </div>
)

const PDFViewerErrorState = () => (
  <div
    className="w-screen flex items-center justify-center"
    style={{ height: 'calc(100vh - 54px)' }}
  >
    <Text color="inverse" variant="body">
      Kesalahan saat memuat dokumen...
    </Text>
  </div>
)

export const PDFViewerEmptyState = () => (
  <div
    className="w-screen flex items-center justify-center bg-black bg-opacity-50"
    style={{ height: 'calc(100vh - 54px)' }}
  >
    <Text color="inverse" variant="body">
      Tidak ada dokumen yang di muat.
    </Text>
  </div>
)

interface PDFViewerDialogProps extends Omit<DialogProps, 'children'> {
  url: string
  name?: string
  className?: string
}

export const PDFViewer = ({ url, className = '' }) => {
  const [wrapperWidth, setWrapperWidth] = useState<number>(0)
  const { width, getElementDimensionById } = useWindowDimensions()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [scale, setScale] = useState(INITIAL_SCALE)
  const file = useMemo(() => ({ url }), [url])

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    enablePinchZoom(scale, setScale, INITIAL_SCALE)
  }

  useEffect(() => {
    const { width: elementWidth } = getElementDimensionById('viewerWrapper')
    setWrapperWidth(elementWidth)
  }, [getElementDimensionById])

  return (
    <div
      id="viewerContainer"
      className={clsx('pdf-viewer-container', className)}
    >
      <div id="viewerWrapper" className="pdf-viewer-wrapper !bg-[#2F3031]">
        <Document
          file={file}
          options={PDF_OPTIONS}
          error={PDFViewerErrorState}
          loading={PDFViewerLoadingState}
          externalLinkTarget="_blank"
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {[...Array(numPages)].map((_, index) => (
            <Page
              key={`pdf-viewer-page-${index}@${scale}`}
              pageNumber={index + 1}
              className="pdf-viewer-page !bg-[#2F3031] flex flex-col items-center"
              renderAnnotationLayer={false}
              renderTextLayer={false}
              scale={scale}
              width={(wrapperWidth || width) - PADDING_SIZE}
            />
          ))}
        </Document>
      </div>
    </div>
  )
}

export default function PDFViewerDialog({
  url,
  name = '',
  ...props
}: PDFViewerDialogProps) {
  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent className="!max-w-full !w-full !h-full !m-0 !rounded-none">
        <DialogCloseButton />
        <DialogHeader
          title={name}
          variant="compact"
          className="!rounded-none"
        />
        <PDFViewer url={url} className="!h-[calc(100vh-60px)]" />
      </DialogContent>
    </Dialog>
  )
}
